import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Loader2, Sparkles, CheckCircle2, AlertCircle } from "lucide-react";
import { extractProfileData } from "@/lib/utils/extractProfileData";
import type { ProfileFormData } from "@/pages/ProfileCreation";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { ChevronDown } from "lucide-react";

interface QuickImportProps {
  onExtract: (data: Partial<ProfileFormData>) => void;
}

export function QuickImport({ onExtract }: QuickImportProps) {
  const [text, setText] = useState("");
  const [isExtracting, setIsExtracting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [extractedFields, setExtractedFields] = useState<string[]>([]);
  const [isOpen, setIsOpen] = useState(false);

  const handleExtract = async () => {
    if (!text.trim() || text.trim().length < 10) {
      setError("Please enter at least 10 characters");
      return;
    }

    setIsExtracting(true);
    setError(null);
    setExtractedFields([]);

    try {
      const data = await extractProfileData(text);
      const fields = Object.keys(data);

      if (fields.length === 0) {
        setError("Couldn't extract any information. Please try adding more details or fill the form manually.");
        return;
      }

      setExtractedFields(fields);
      onExtract(data);

      // Auto-collapse after successful extraction
      setTimeout(() => setIsOpen(false), 2000);
    } catch (err) {
      console.error("quick_import.extraction_failed");
      setError(err instanceof Error ? err.message : "Failed to extract data. Please try again.");
    } finally {
      setIsExtracting(false);
    }
  };

  const handleClear = () => {
    setText("");
    setError(null);
    setExtractedFields([]);
  };

  return (
    <Collapsible open={isOpen} onOpenChange={setIsOpen} className="w-full">
      <Card className="border-primary/20">
        <CollapsibleTrigger asChild>
          <CardHeader className="cursor-pointer hover:bg-accent/50 transition-colors">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Sparkles className="h-5 w-5 text-primary" />
                <CardTitle className="text-lg">Quick Import (Optional)</CardTitle>
              </div>
              <ChevronDown className={`h-5 w-5 text-muted-foreground transition-transform ${isOpen ? 'rotate-180' : ''}`} />
            </div>
            <CardDescription>
              Paste any existing bio, resume, or profile and we'll auto-fill the form for you
            </CardDescription>
          </CardHeader>
        </CollapsibleTrigger>
        
        <CollapsibleContent>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Textarea
                placeholder="Example: I'm Sarah, 28, software engineer in Austin. Grew up Muslim, pray 5x daily. Masters from UT Austin. Love hiking and cooking. Looking for someone practicing and family-oriented..."
                value={text}
                onChange={(e) => {
                  setText(e.target.value);
                  setError(null);
                }}
                className="min-h-[120px] resize-none"
                disabled={isExtracting}
                maxLength={5000}
              />
              <p className="text-xs text-muted-foreground">
                {text.length}/5000 characters
              </p>
            </div>

            {error && (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}

            {extractedFields.length > 0 && (
              <Alert className="border-primary/50 bg-primary/5">
                <CheckCircle2 className="h-4 w-4 text-primary" />
                <AlertDescription>
                  <strong>Success!</strong> Extracted {extractedFields.length} field(s): {" "}
                  {extractedFields.slice(0, 5).join(", ")}
                  {extractedFields.length > 5 && ` +${extractedFields.length - 5} more`}
                </AlertDescription>
              </Alert>
            )}

            <div className="flex gap-2">
              <Button
                onClick={handleExtract}
                disabled={isExtracting || !text.trim()}
                className="flex-1"
              >
                {isExtracting ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Extracting...
                  </>
                ) : (
                  <>
                    <Sparkles className="mr-2 h-4 w-4" />
                    Extract Info
                  </>
                )}
              </Button>
              <Button
                variant="outline"
                onClick={handleClear}
                disabled={isExtracting || !text}
              >
                Clear
              </Button>
            </div>

            <p className="text-xs text-muted-foreground">
              💡 Tip: The AI will only extract information explicitly mentioned. Review and edit all fields after extraction.
            </p>
          </CardContent>
        </CollapsibleContent>
      </Card>
    </Collapsible>
  );
}
